import { useRef } from 'react'
import classNames from 'classnames'
import debounce from 'lodash/debounce'

import { EVENT_NAME_WHEEL } from '../../constants/event'
import { useEventListener } from '../../hooks'
import { preventDefault } from '../../utils/events'

import { SCROLL_SLIDER_DEBOUNCE_TIME } from './ScrollSlider.constants'
import {
  useScrollSliderActions,
  useScrollSliderButtons,
} from './ScrollSlider.hooks'
import { getScrollSliderWheelOffset } from './ScrollSlider.utils'
import { ScrollSliderButtons } from './ScrollSliderButtons'
import { ScrollSliderProvider } from './ScrollSliderProvider'

import * as styles from './ScrollSlider.module.css'

function ScrollSlider({
  Buttons = ScrollSliderButtons,
  children,
  className = styles.ScrollSlider,
  contentClassName = styles.ScrollSliderContent,
  isLoading,
  itemGap,
  itemWidth,
  withWheelScroll,
}) {
  const sliderRef = useRef(null)

  const {
    handleClickNext,
    handleClickPrev,
    isNextBtnVisible,
    isPrevBtnVisible,
    updateBtnsVisible,
  } = useScrollSliderButtons({
    itemGap,
    itemWidth,
    sliderRef,
  })

  const {
    handleClickSlide,
    handleMouseDown,
    handleSliderContentClick,
    sliderGrabbing,
  } = useScrollSliderActions({ itemWidth, sliderRef })

  function handleScroll() {
    updateBtnsVisible()
  }

  function handleWheel(event) {
    const slider = sliderRef.current

    const offset = slider?.contains(event.target)
      ? getScrollSliderWheelOffset({ event, slider })
      : 0

    if (offset) {
      preventDefault(event)
      slider.scrollBy({ behavior: 'instant', left: offset })
    }
  }

  useEventListener({
    elementRef: sliderRef,
    eventName: EVENT_NAME_WHEEL,
    handler: withWheelScroll ? handleWheel : null,
  })

  const debouncedSliderScroll = Buttons
    ? debounce(handleScroll, SCROLL_SLIDER_DEBOUNCE_TIME)
    : null

  return (
    <div
      className={classNames(className, {
        [styles.grabbing]: sliderGrabbing,
      })}
    >
      <ScrollSliderProvider
        handleClickSlide={handleClickSlide}
        itemWidth={itemWidth}
        sliderRef={sliderRef}
      >
        {isLoading ? (
          children
        ) : (
          <>
            <div
              className={classNames(contentClassName, {
                [styles.grabbing]: sliderGrabbing,
              })}
              ref={sliderRef}
              onClickCapture={handleSliderContentClick}
              onMouseDown={handleMouseDown}
              onScroll={debouncedSliderScroll}
            >
              {children}
            </div>
            {Buttons ? (
              <Buttons
                isNextBtnVisible={isNextBtnVisible}
                isPrevBtnVisible={isPrevBtnVisible}
                onClickNextBtn={handleClickNext}
                onClickPrevBtn={handleClickPrev}
              />
            ) : null}
          </>
        )}
      </ScrollSliderProvider>
    </div>
  )
}

export default ScrollSlider
